// =====================================================================================
// Colour identities for the "Full Modern" application theme. A company picks one
// (custom setting AppThemeColor) and it drives the ENTIRE app: canvas gradient,
// sidebar gradient, header bar, primary accent, buttons, tabs, focus rings.
//
// The ids intentionally match the Modern Dashboard launcher themes so a store can
// run the same identity on the launcher and the rest of the product.
// =====================================================================================

export interface ModernPalette {
  id: string;
  name: string;
  /**
   * "Paper" identity: the canvas stays LIGHT even in immersive mode, and the
   * transparent chrome switches to dark text. For professional light looks
   * (off-white, smoke, platinum…) where a dark page would defeat the point.
   */
  isLight?: boolean;
  primary: string;
  primaryLight: string;
  primaryDark: string;
  /** Gradient end colour for accents (buttons, active nav pill, gradient text). */
  second: string;
  /** Dark gradient for the sidebar (and the whole canvas in Full Gradient mode). */
  sidebar: string;
  /** Header bar — MUST be a plain colour (the Header wraps it in alpha()). */
  header: string;
  /** Light-mode page canvas. */
  lightBase: string;
  lightCanvas: string;
  /** Dark / Full-Gradient page canvas. */
  darkBase: string;
  darkCanvas: string;
  swatch: [string, string];
}

const canvasLight = (a: string, b: string, base: string, top: string) =>
  `radial-gradient(1000px 560px at 88% -12%, ${a} 0%, transparent 60%),` +
  `radial-gradient(900px 540px at -12% 108%, ${b} 0%, transparent 58%),` +
  `linear-gradient(180deg, ${base} 0%, ${top} 100%)`;

const canvasDark = (a: string, b: string, from: string, to: string) =>
  `radial-gradient(1000px 560px at 88% -12%, ${a} 0%, transparent 60%),` +
  `radial-gradient(900px 540px at -12% 108%, ${b} 0%, transparent 58%),` +
  `linear-gradient(180deg, ${from} 0%, ${to} 100%)`;

export const MODERN_PALETTES: ModernPalette[] = [
  {
    id: 'violet',
    name: 'Midnight Violet',
    primary: '#7c3aed', primaryLight: '#a78bfa', primaryDark: '#5b21b6', second: '#4f46e5',
    sidebar: 'linear-gradient(195deg, #1e1b4b 0%, #171a3a 55%, #0f172a 100%)',
    header: '#1e1b4b',
    lightBase: '#f6f5fd',
    lightCanvas: canvasLight('rgba(124,58,237,0.20)', 'rgba(37,99,235,0.18)', '#f6f5fd', '#eef0fb'),
    darkBase: '#0b1020',
    darkCanvas: canvasDark('rgba(124,58,237,0.30)', 'rgba(37,99,235,0.22)', '#1e1b4b', '#0b1020'),
    swatch: ['#7c3aed', '#1e1b4b']
  },
  {
    id: 'ocean',
    name: 'Deep Ocean',
    primary: '#0284c7', primaryLight: '#7dd3fc', primaryDark: '#075985', second: '#2563eb',
    sidebar: 'linear-gradient(195deg, #0c4a6e 0%, #0b3a56 55%, #082f49 100%)',
    header: '#0c4a6e',
    lightBase: '#f1f8fd',
    lightCanvas: canvasLight('rgba(2,132,199,0.20)', 'rgba(37,99,235,0.16)', '#f1f8fd', '#e8f1fb'),
    darkBase: '#06192b',
    darkCanvas: canvasDark('rgba(14,116,144,0.34)', 'rgba(30,64,175,0.26)', '#0c4a6e', '#06192b'),
    swatch: ['#0ea5e9', '#082f49']
  },
  {
    id: 'sunset',
    name: 'Sunset Ember',
    primary: '#ea580c', primaryLight: '#fdba74', primaryDark: '#9a3412', second: '#e11d48',
    sidebar: 'linear-gradient(195deg, #431407 0%, #2a1410 55%, #1c1917 100%)',
    header: '#431407',
    lightBase: '#fff7f2',
    lightCanvas: canvasLight('rgba(234,88,12,0.18)', 'rgba(225,29,72,0.14)', '#fff7f2', '#fdeee6'),
    darkBase: '#16100e',
    darkCanvas: canvasDark('rgba(194,65,12,0.34)', 'rgba(157,23,77,0.26)', '#431407', '#16100e'),
    swatch: ['#f97316', '#1c1917']
  },
  {
    id: 'emerald',
    name: 'Emerald Forest',
    primary: '#059669', primaryLight: '#6ee7b7', primaryDark: '#047857', second: '#0d9488',
    sidebar: 'linear-gradient(195deg, #064e3b 0%, #04382c 55%, #022c22 100%)',
    header: '#064e3b',
    lightBase: '#f1fbf6',
    lightCanvas: canvasLight('rgba(5,150,105,0.18)', 'rgba(13,148,136,0.16)', '#f1fbf6', '#e7f6ef'),
    darkBase: '#04201a',
    darkCanvas: canvasDark('rgba(4,120,87,0.34)', 'rgba(15,118,110,0.26)', '#064e3b', '#04201a'),
    swatch: ['#10b981', '#022c22']
  },
  {
    id: 'royal',
    name: 'Royal Indigo',
    primary: '#4f46e5', primaryLight: '#a5b4fc', primaryDark: '#3730a3', second: '#7c3aed',
    sidebar: 'linear-gradient(195deg, #312e81 0%, #272566 55%, #1e1b4b 100%)',
    header: '#312e81',
    lightBase: '#f4f5fd',
    lightCanvas: canvasLight('rgba(79,70,229,0.20)', 'rgba(124,58,237,0.16)', '#f4f5fd', '#ecedfb'),
    darkBase: '#0d0b26',
    darkCanvas: canvasDark('rgba(109,40,217,0.32)', 'rgba(67,56,202,0.26)', '#312e81', '#0d0b26'),
    swatch: ['#6d28d9', '#1e1b4b']
  },
  {
    id: 'cherry',
    name: 'Cherry Noir',
    primary: '#e11d48', primaryLight: '#fda4af', primaryDark: '#9f1239', second: '#be123c',
    sidebar: 'linear-gradient(195deg, #4c0519 0%, #300f14 55%, #1c1917 100%)',
    header: '#4c0519',
    lightBase: '#fff5f7',
    lightCanvas: canvasLight('rgba(225,29,72,0.18)', 'rgba(190,18,60,0.14)', '#fff5f7', '#fbe9ee'),
    darkBase: '#170a0e',
    darkCanvas: canvasDark('rgba(159,18,57,0.36)', 'rgba(124,45,18,0.24)', '#4c0519', '#170a0e'),
    swatch: ['#9f1239', '#1c1917']
  },
  {
    id: 'graphite',
    name: 'Graphite',
    primary: '#475569', primaryLight: '#94a3b8', primaryDark: '#1e293b', second: '#334155',
    sidebar: 'linear-gradient(195deg, #1e293b 0%, #172033 55%, #0b1220 100%)',
    header: '#1e293b',
    lightBase: '#f5f7fa',
    lightCanvas: canvasLight('rgba(71,85,105,0.16)', 'rgba(15,23,42,0.10)', '#f5f7fa', '#eaeef5'),
    darkBase: '#080d17',
    darkCanvas: canvasDark('rgba(51,65,85,0.40)', 'rgba(30,41,59,0.30)', '#1e293b', '#080d17'),
    swatch: ['#475569', '#0b1220']
  },
  {
    id: 'aurora',
    name: 'Aurora',
    primary: '#0d9488', primaryLight: '#5eead4', primaryDark: '#0f766e', second: '#7c3aed',
    sidebar: 'linear-gradient(195deg, #134e4a 0%, #172554 55%, #0f172a 100%)',
    header: '#123244',
    lightBase: '#f1fbfa',
    lightCanvas: canvasLight('rgba(13,148,136,0.18)', 'rgba(124,58,237,0.16)', '#f1fbfa', '#eaf3fb'),
    darkBase: '#07131f',
    darkCanvas: canvasDark('rgba(13,148,136,0.30)', 'rgba(124,58,237,0.28)', '#0f2f3d', '#07131f'),
    swatch: ['#0d9488', '#7c3aed']
  }
];

// ── Professional light "paper" identities ───────────────────────────────────
// These keep a light canvas even in immersive mode (isLight), with dark text on
// the transparent sidebar/header — clean, quiet, business-like.

MODERN_PALETTES.push({
  id: 'offwhite',
  name: 'Pure Off-White',
  isLight: true,
  primary: '#4f46e5', primaryLight: '#a5b4fc', primaryDark: '#3730a3', second: '#6366f1',
  sidebar: 'linear-gradient(195deg, #fbfbfd 0%, #f5f6f9 60%, #eef0f4 100%)',
  header: '#ffffff',
  lightBase: '#fcfcfd',
  lightCanvas:
    'radial-gradient(60% 100% at 50% -10%, #ffffff 0%, rgba(255,255,255,0) 70%),' +
    'linear-gradient(180deg, #fdfdfe 0%, #f5f6f8 100%)',
  darkBase: '#101218',
  darkCanvas: 'linear-gradient(180deg, #1a1c22 0%, #101218 100%)',
  swatch: ['#ffffff', '#e6e8ee']
});

MODERN_PALETTES.push({
  id: 'smoke',
  name: 'Smokey',
  isLight: true,
  primary: '#475569', primaryLight: '#94a3b8', primaryDark: '#1e293b', second: '#334155',
  sidebar: 'linear-gradient(195deg, #f4f6f8 0%, #eceff3 60%, #e3e7ed 100%)',
  header: '#f7f8fa',
  lightBase: '#f2f4f7',
  lightCanvas:
    'radial-gradient(70% 90% at 78% -10%, rgba(100,116,139,0.16) 0%, rgba(100,116,139,0) 62%),' +
    'radial-gradient(60% 80% at -10% 105%, rgba(148,163,184,0.18) 0%, rgba(148,163,184,0) 58%),' +
    'linear-gradient(180deg, #f7f8fa 0%, #e9ecf1 100%)',
  darkBase: '#0f1319',
  darkCanvas: 'linear-gradient(180deg, #1c2029 0%, #0f1319 100%)',
  swatch: ['#e9ecf1', '#94a3b8']
});

MODERN_PALETTES.push({
  id: 'platinum',
  name: 'Platinum',
  isLight: true,
  primary: '#0284c7', primaryLight: '#7dd3fc', primaryDark: '#075985', second: '#0ea5e9',
  sidebar: 'linear-gradient(195deg, #f6f8fb 0%, #eef2f7 60%, #e5eaf2 100%)',
  header: '#f8fafc',
  lightBase: '#f4f7fb',
  lightCanvas:
    'radial-gradient(70% 90% at 82% -10%, rgba(2,132,199,0.12) 0%, rgba(2,132,199,0) 60%),' +
    'linear-gradient(180deg, #f8fbfe 0%, #e9eef6 100%)',
  darkBase: '#0a1420',
  darkCanvas: 'linear-gradient(180deg, #12202f 0%, #0a1420 100%)',
  swatch: ['#eef2f7', '#0284c7']
});

MODERN_PALETTES.push({
  id: 'sandstone',
  name: 'Sandstone',
  isLight: true,
  primary: '#b45309', primaryLight: '#fcd34d', primaryDark: '#92400e', second: '#d97706',
  sidebar: 'linear-gradient(195deg, #faf7f2 0%, #f4efe6 60%, #ece5d8 100%)',
  header: '#fdfbf7',
  lightBase: '#faf7f2',
  lightCanvas:
    'radial-gradient(70% 90% at 80% -10%, rgba(180,83,9,0.10) 0%, rgba(180,83,9,0) 60%),' +
    'linear-gradient(180deg, #fdfbf7 0%, #f2ece1 100%)',
  darkBase: '#17120c',
  darkCanvas: 'linear-gradient(180deg, #2a2118 0%, #17120c 100%)',
  swatch: ['#f2ece1', '#b45309']
});

MODERN_PALETTES.push({
  id: 'nordic',
  name: 'Nordic Steel',
  isLight: true,
  primary: '#1d4ed8', primaryLight: '#93c5fd', primaryDark: '#1e3a8a', second: '#2563eb',
  sidebar: 'linear-gradient(195deg, #f5f7fb 0%, #edf1f8 60%, #e3e9f4 100%)',
  header: '#f8fafc',
  lightBase: '#f2f5fa',
  lightCanvas:
    'radial-gradient(70% 90% at 80% -10%, rgba(29,78,216,0.10) 0%, rgba(29,78,216,0) 60%),' +
    'linear-gradient(180deg, #f8fafd 0%, #e8edf6 100%)',
  darkBase: '#0a0f1c',
  darkCanvas: 'linear-gradient(180deg, #131b2e 0%, #0a0f1c 100%)',
  swatch: ['#e8edf6', '#1d4ed8']
});

// Clean near-white canvas with a soft light streak down the middle.
MODERN_PALETTES.push({
  id: 'frost',
  name: 'Frost Paper',
  isLight: true,
  primary: '#4f46e5', primaryLight: '#a5b4fc', primaryDark: '#3730a3', second: '#6366f1',
  sidebar: 'linear-gradient(195deg, #fafbfc 0%, #f3f4f6 60%, #eaecef 100%)',
  header: '#fbfcfd',
  lightBase: '#f4f5f6',
  lightCanvas:
    'radial-gradient(58% 120% at 50% -10%, #ffffff 0%, rgba(255,255,255,0) 72%),' +
    'linear-gradient(90deg, #eff0f2 0%, #f7f8f9 32%, #ffffff 50%, #f7f8f9 68%, #eff0f2 100%)',
  darkBase: '#0f1117',
  darkCanvas:
    'radial-gradient(58% 120% at 50% -10%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 70%),' +
    'linear-gradient(90deg, #12141b 0%, #171a22 35%, #1c2029 50%, #171a22 65%, #12141b 100%)',
  swatch: ['#f7f8f9', '#20242e']
});

export const DEFAULT_PALETTE_ID = 'violet';

export const paletteById = (id: string | null | undefined): ModernPalette =>
  MODERN_PALETTES.find((p) => p.id === id) || MODERN_PALETTES[0];

/**
 * 'gradient' = fully immersive: ONE gradient across the page with a transparent
 *              sidebar and header (the Modern Dashboard look) — the default.
 * 'light'    = light page canvas with dark chrome, for stores that prefer it.
 */
export type ModernSurface = 'light' | 'gradient';
export const DEFAULT_SURFACE: ModernSurface = 'gradient';
